import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
} from '@mui/icons-material';
import { orderService } from '../../services/ris';
import { formatDicomDate, calculateAge } from '../../services/utils';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const satoshiBold = {
  fontFamily: '"Satoshi", sans-serif',
  fontWeight: 700,
};
const satoshiMedium = {
  fontFamily: '"Satoshi", sans-serif',
  fontWeight: 500,
};
const satoshiRegular = {
  fontFamily: '"Satoshi", sans-serif',
  fontWeight: 400,
};

export default function PatientProfileView({ patient, onBack, onCreateOrder, onViewOrder, setCurrentView }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const inputBg = isDark ? '#121212' : '#F8FAFC';
  const cardBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const cardBorder = `1px solid ${cardBorderColor}`;
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#64748B';
  const textColorMuted = isDark ? '#94A3B8' : '#94A3B8';
  const hoverBgSubtle = isDark ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC';
  const tableHeaderBg = isDark ? '#18181B' : '#F8FAFC';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const mrn = patient?.mrn || patient?.patientId || '-';
  const patientName = patient
    ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.name || 'Unknown Patient'
    : '-';
  const gender = patient?.sex || patient?.patientSex || '-';
  
  const getInitials = (name) => {
    if (!name || name === '-') return 'P';
    return name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!patient) return;
      setLoading(true);
      try {
        const mrnVal = patient.mrn || patient.patientId;
        if (mrnVal) {
          const res = await orderService.list({ mrn: mrnVal });
          setOrders(res.data?.orders || []);
        }
      } catch (err) {
        console.error('Failed to fetch patient orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [patient]);

  const filteredOrders = orders.filter((o) => {
    const q = searchTerm.toLowerCase();
    const orderId = String(o.orderId || o._id || '').toLowerCase();
    const modality = String(o.modality || '').toLowerCase();
    const exam = String(o.procedureDescription || o.studyDescription || '').toLowerCase();
    return orderId.includes(q) || modality.includes(q) || exam.includes(q);
  });

  const getStatusStyle = (status) => {
    const s = String(status || '').toUpperCase();
    if (['COMPLETED', 'REPORTED', 'IMAGES_AVAILABLE'].includes(s)) {
      return {
        bg: isDark ? 'rgba(16, 185, 129, 0.18)' : '#ECFDF5',
        fg: isDark ? '#34D399' : '#059669',
        label: 'Completed'
      };
    }
    if (['STARTED', 'IN_EXAM', 'SCANNING', 'AWAITING_REPORT', 'TOREPORT', 'PRELIM', 'IN_PROGRESS', 'SCHEDULED'].includes(s)) {
      return {
        bg: isDark ? 'rgba(245, 158, 11, 0.18)' : '#FEF3C7',
        fg: isDark ? '#FBBF24' : '#D97706',
        label: 'In Progress'
      };
    }
    return {
      bg: isDark ? 'rgba(59, 130, 246, 0.18)' : '#EFF6FF',
      fg: isDark ? '#60A5FA' : '#2563EB',
      label: 'New Order'
    };
  };

  const formatDOB = (dobString) => {
    if (!dobString) return '-';
    const dobFormatted = formatDicomDate(dobString);
    if (dobFormatted === 'Unknown') return dobString;
    const age = calculateAge(dobString);
    return `${dobFormatted} (${age} Years)`;
  };

  return (
    <Box sx={{ p: 0, fontFamily: '"Satoshi", sans-serif', textAlign: 'left' }}>
      {/* Header section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={onBack}
            sx={{
              color: textColorPrimary,
              border: cardBorder,
              borderRadius: '6px',
              width: 32,
              height: 32,
              p: 0.5,
              bgcolor: cardBg,
              '&:hover': {
                backgroundColor: hoverBgSubtle,
                borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Box>
            <Typography
              variant="h5"
              sx={{
                ...satoshiBold,
                color: textColorPrimary,
                fontSize: '1.05rem',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                mb: 0.25,
              }}
            >
              Patient Profile
            </Typography>
            <Typography variant="body2" sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.78rem' }}>
              View patient details and order history.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => onCreateOrder(patient)}
          startIcon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
          sx={{
            ...satoshiBold,
            textTransform: 'none',
            color: '#FFFFFF',
            bgcolor: '#00665C',
            borderRadius: '6px',
            px: 1.75,
            height: '32px',
            fontSize: '0.8rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#004D47',
              boxShadow: 'none',
            },
          }}
        >
          Create New Order
        </Button>
      </Box>

      {/* Card 1: Patient Summary */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '10px',
          border: cardBorder,
          bgcolor: cardBg,
          mb: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.01)',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            ...satoshiBold,
            color: textColorPrimary,
            fontSize: '0.85rem',
            mb: 1.5,
          }}
        >
          Patient Summary
        </Typography>

        <Grid container spacing={2} alignItems="center">
          {/* Circular initials avatar */}
          <Grid item xs={12} md={3.5} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#EFF6FF',
                color: isDark ? '#60A5FA' : '#2563EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                fontWeight: 800,
                flexShrink: 0,
                border: isDark ? '1.5px solid rgba(59, 130, 246, 0.4)' : '1.5px solid #DBEAFE',
              }}
            >
              {getInitials(patientName)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <Typography variant="h6" noWrap sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.95rem' }}>
                  {patientName}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    px: 0.85,
                    py: 0.15,
                    borderRadius: '4px',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: isDark
                      ? (gender === 'M' || gender === 'Male' ? 'rgba(37, 99, 235, 0.2)' : gender === 'F' || gender === 'Female' ? 'rgba(219, 39, 119, 0.2)' : 'rgba(255, 255, 255, 0.08)')
                      : (gender === 'M' || gender === 'Male' ? '#EFF6FF' : gender === 'F' || gender === 'Female' ? '#FDF2F8' : '#F1F5F9'),
                    color: isDark
                      ? (gender === 'M' || gender === 'Male' ? '#60A5FA' : gender === 'F' || gender === 'Female' ? '#F472B6' : '#94A3B8')
                      : (gender === 'M' || gender === 'Male' ? '#2563EB' : gender === 'F' || gender === 'Female' ? '#DB2777' : '#475569'),
                  }}
                >
                  {gender === 'M' || gender === 'Male' ? 'Male' : gender === 'F' || gender === 'Female' ? 'Female' : gender}
                </Box>
              </Box>
              <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.75rem' }}>
                Patient ID: <span style={{ color: textColorPrimary, fontWeight: 700 }}>{mrn}</span>
              </Typography>
            </Box>
          </Grid>

          {/* Details layout columns */}
          <Grid item xs={12} md={8.5}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.72rem', mb: 0.25 }}>
                  Date of Birth
                </Typography>
                <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.8rem' }}>
                  {formatDOB(patient?.birthDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.72rem', mb: 0.25 }}>
                  Phone Number
                </Typography>
                <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.8rem' }}>
                  {patient?.telephone || patient?.phone || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.72rem', mb: 0.25 }}>
                  Address
                </Typography>
                <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.8rem' }} noWrap>
                  {patient?.address || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Card 2: Visit / Order History */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '10px',
          border: cardBorder,
          bgcolor: cardBg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.01)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography variant="subtitle2" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.85rem' }}>
              Visit / Order History
            </Typography>
            <Box
              sx={{
                px: 1,
                py: 0.15,
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#F1F5F9',
                color: textColorSecondary,
                borderRadius: '10px',
                fontSize: '0.68rem',
                fontWeight: 700,
              }}
            >
              {filteredOrders.length} {filteredOrders.length === 1 ? 'Record' : 'Records'}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  backgroundColor: inputBg,
                  border: cardBorder,
                  height: 30,
                  px: 1.25,
                  color: textColorPrimary,
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1',
                  },
                  '&.Mui-focused': {
                    backgroundColor: cardBg,
                    border: '1.5px solid #0D9488',
                  },
                },
                '& .MuiInputBase-input': {
                  color: textColorPrimary,
                  fontSize: '0.78rem',
                  py: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: textColorMuted, fontSize: 15 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="outlined"
              startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: '16px',
                borderColor: cardBorderColor,
                color: textColorPrimary,
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'none',
                height: 30,
                px: 1.5,
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1',
                  bgcolor: hoverBgSubtle,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: tableHeaderBg }}>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder }}>Visit Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder }}>Modality</TableCell>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder }}>Examination</TableCell>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder }}>Ordering Physician</TableCell>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, py: '6px !important', px: 1.25, fontSize: '0.72rem', borderBottom: cardBorder, textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: cardBorder }}>
                    <CircularProgress size={24} sx={{ color: '#0D9488' }} />
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: textColorSecondary, fontSize: '0.8rem', borderBottom: cardBorder }}>
                    No visit or order history found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((o) => {
                  const statusStyle = getStatusStyle(o.status || o.orderStatus || o.workflowStatus);
                  const visitDate = o.createdAt || o.orderedAt || o.studyDate;
                  const formattedDate = visitDate
                    ? new Date(visitDate).toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                      })
                    : '-';
                  const orderId = o.orderId || o._id || '-';
                  const orderIdDisplay = typeof orderId === 'string' && orderId.length > 8
                    ? `OR-${orderId.slice(-6).toUpperCase()}`
                    : orderId;

                  const referringDoctor = o.referrerName || (o.referrer ? `${o.referrer.firstName || ''} ${o.referrer.lastName || ''}`.trim() : null) || o.orderingPhysician || '-';

                  return (
                    <TableRow key={o._id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: '4px !important', px: 1.25, color: textColorSecondary, fontSize: '0.75rem', fontWeight: 500, borderBottom: cardBorder }}>{formattedDate}</TableCell>
                      <TableCell sx={{ py: '4px !important', px: 1.25, color: isDark ? '#60A5FA' : '#2563EB', fontSize: '0.75rem', fontWeight: 700, borderBottom: cardBorder }}>{orderIdDisplay}</TableCell>
                      <TableCell sx={{ py: '4px !important', px: 1.25, color: textColorPrimary, fontSize: '0.75rem', fontWeight: 700, borderBottom: cardBorder }}>{o.modality || 'CT'}</TableCell>
                      <TableCell sx={{ py: '4px !important', px: 1.25, color: textColorSecondary, fontSize: '0.75rem', fontWeight: 500, borderBottom: cardBorder }}>{o.procedureDescription || o.studyDescription || '-'}</TableCell>
                      <TableCell sx={{ py: '4px !important', px: 1.25, color: textColorSecondary, fontSize: '0.75rem', fontWeight: 500, borderBottom: cardBorder }}>{referringDoctor}</TableCell>
                      <TableCell sx={{ py: '4px !important', px: 1.25, borderBottom: cardBorder }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.25,
                            py: 0.2,
                            borderRadius: '10px',
                            bgcolor: statusStyle.bg,
                            color: statusStyle.fg,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                          }}
                        >
                          {statusStyle.label}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: '4px !important', px: 1.25, textAlign: 'center', borderBottom: cardBorder }}>
                        <IconButton
                          size="small"
                          onClick={() => onViewOrder(o)}
                          sx={{
                            color: textColorSecondary,
                            border: cardBorder,
                            borderRadius: '4px',
                            width: 24,
                            height: 24,
                            p: 0,
                            '&:hover': {
                              backgroundColor: hoverBgSubtle,
                              color: '#0D9488',
                              borderColor: '#0D9488',
                            },
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
